// Basemap styles. All open data, and none needs an API key or an account:
//  * OpenFreeMap (vector, OpenMapTiles schema) — free for any use, attribution
//    required. Replaces CARTO, whose raster tiles started answering every
//    request with an "API KEY REQUIRED" placeholder.
//  * OpenStreetMap's own Standard raster — the classic, densest OSM look.
//    Opt-in only, to keep load on OSM's volunteer-run tile servers light.
//
// Every choice has a light and a dark variant so the map follows the app theme.

export const OFM_STYLES = 'https://tiles.openfreemap.org/styles'

export const OFM_ATTRIBUTION =
  '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a> ' +
  '<a href="https://www.openmaptiles.org/" target="_blank" rel="noopener">© OpenMapTiles</a> ' +
  'Data from <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>'

const OSM_ATTRIBUTION =
  '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors'

/** Basemap ids, in picker order. 'streets' keeps its old id so stored choices survive. */
export const BASEMAP_IDS = ['minimal', 'detailed', 'streets']

/** OSM Standard as a one-layer raster style; dimmed (not inverted) in dark mode. */
export function osmRasterStyle(theme = 'light') {
  return {
    version: 8,
    sources: {
      osm: {
        type: 'raster',
        tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
        tileSize: 256,
        maxzoom: 19,
        attribution: OSM_ATTRIBUTION,
      },
    },
    layers: [
      {
        id: 'osm',
        type: 'raster',
        source: 'osm',
        paint:
          theme === 'dark'
            ? { 'raster-brightness-max': 0.62, 'raster-saturation': -0.4, 'raster-contrast': 0.1 }
            : {},
      },
    ],
  }
}

/**
 * The style to load for a basemap choice in a theme: a style URL (OpenFreeMap)
 * or an inline style object (OSM raster).
 */
export function basemapStyle(id, theme = 'light') {
  const dark = theme === 'dark'
  if (id === 'streets') return osmRasterStyle(theme)
  if (id === 'detailed') return `${OFM_STYLES}/${dark ? 'fiord' : 'liberty'}`
  return `${OFM_STYLES}/${dark ? 'dark' : 'positron'}`
}

/**
 * A basemap label expression in the app's language. OpenFreeMap styles prefer
 * English names ("Prague", "Old Town"); a Czech driver wants the names on the
 * street signs ("Praha", "Staré Město"). Returns null for labels that aren't
 * names (road-number shields), which keep the style's own expression.
 */
export function localizedTextField(textField, lang) {
  if (!/"name(?:_en|:latin|:nonlatin)?"/.test(JSON.stringify(textField ?? null))) return null
  return lang === 'en'
    ? ['coalesce', ['get', 'name:en'], ['get', 'name_en'], ['get', 'name']]
    : ['coalesce', ['get', 'name:cs'], ['get', 'name']]
}

/**
 * Buildings stay flat. Liberty raises its own in 3D from z14: shaded walls that
 * lean out from the middle of the screen, over the very streets the sections
 * colour. So its 3D layer goes, and its flat buildings, drawn only up to z14,
 * carry on in their place. Returns the 3D layers to remove and the flat ones
 * to keep showing at every zoom; nothing for a style that is flat already.
 */
export function flatBuildings(layers) {
  const buildings = layers.filter((l) => l['source-layer'] === 'building')
  const remove = buildings.filter((l) => l.type === 'fill-extrusion')
  if (!remove.length) return { remove, extend: [] }
  return { remove, extend: buildings.filter((l) => l.type === 'fill' && l.maxzoom != null) }
}

/**
 * Where the sections go in a basemap's layer stack, so they read as the streets
 * they are. The city's polygons are coarse and spill over whole blocks, so they
 * go above every fill and line (the street is tinted kerb to kerb), close up a
 * copy of the buildings goes back on top of them (trimming them to the street,
 * see buildingsOver), and every label stays above both. Some styles slot a few
 * labels in early; those are lifted. Returns the label layer to insert before,
 * the labels to lift, and the building layers to copy over the sections.
 */
export function zoneStacking(layers) {
  let last = -1
  layers.forEach((l, i) => {
    if (l.type !== 'symbol') last = i
  })
  return {
    before: layers[last + 1]?.id,
    lift: layers.slice(0, last + 1).filter((l) => l.type === 'symbol').map((l) => l.id),
    buildings: layers.filter((l) => l['source-layer'] === 'building' && l.type === 'fill'),
  }
}

// Close up, buildings drawn over the sections trim them to their streets.
// Further out a street is only a few pixels wide and the city would read as
// buildings, so there the sections show whole and the trim fades in between.
export const TRIM_ZOOMS = [14.5, 15.5]

/**
 * The copy of a basemap building layer laid over the sections: the same
 * buildings, fading in over TRIM_ZOOMS (the basemap's own layer stays put,
 * under the sections). Null for a layer that is gone by then.
 */
export function buildingsOver(layer) {
  const [from, to] = TRIM_ZOOMS
  if (layer.maxzoom != null && layer.maxzoom <= from) return null
  return {
    ...layer,
    id: `${layer.id}-over-zones`,
    minzoom: Math.max(layer.minzoom ?? 0, from),
    paint: { ...layer.paint, 'fill-opacity': ['interpolate', ['linear'], ['zoom'], from, 0, to, 1] },
  }
}

/** Extra attribution the style itself doesn't carry (OpenFreeMap's is empty). */
export function basemapAttribution(id) {
  return id === 'streets' ? undefined : OFM_ATTRIBUTION
}
